"use client";

import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

interface LanguageStatsProps {
  languages: { [key: string]: number };
}

// GitHub-like colors for programming languages
const languageColors: { [key: string]: string } = {
  TypeScript: "#3178c6",
  JavaScript: "#f1e05a",
  Python: "#3572A5",
  Java: "#b07219",
  "C++": "#f34b7d",
  C: "#555555",
  "C#": "#178600",
  Go: "#00ADD8",
  Rust: "#dea584",
  Ruby: "#701516",
  PHP: "#4F5D95",
  Swift: "#F05138",
  Kotlin: "#A97BFF",
  Dart: "#00B4AB",
  HTML: "#e34c26",
  CSS: "#563d7c",
  Shell: "#89e051",
  Vue: "#41b883",
  React: "#61dafb",
  "Jupyter Notebook": "#DA5B0B",
  Markdown: "#083fa1",
};

const getLanguageColor = (lang: string): string => {
  return languageColors[lang] || "#8b949e";
};

const formatBytes = (bytes: number): string => {
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toFixed(1)}M`;
  }
  if (bytes >= 1_000) {
    return `${(bytes / 1_000).toFixed(1)}k`;
  }
  return bytes.toString();
};

export function LanguageStats({ languages }: LanguageStatsProps) {
  const { t } = useTranslation();
  const totalBytes = Object.values(languages).reduce((sum, bytes) => sum + bytes, 0);
  const sortedLanguages = Object.entries(languages)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  if (sortedLanguages.length === 0) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Icon icon="solar:code-file-linear" className="w-5 h-5 text-primary" />
          {t("Stats.languages.title")}
        </h3>
        <div className="text-center py-8 text-muted-foreground">
          {t("Stats.languages.noData")}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border/50 bg-card p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Icon icon="solar:code-file-linear" className="w-5 h-5 text-primary" />
        {t("Stats.languages.title")}
      </h3>

      {/* Progress bar */}
      <div className="flex h-3 rounded-full overflow-hidden mb-6">
        {sortedLanguages.map(([lang, bytes]) => {
          const percentage = (bytes / totalBytes) * 100;
          return (
            <div
              key={lang}
              className="h-full transition-all duration-300"
              style={{
                width: `${percentage}%`,
                backgroundColor: getLanguageColor(lang),
              }}
              title={`${lang}: ${percentage.toFixed(1)}%`}
            />
          );
        })}
      </div>

      {/* Language list */}
      <div className="space-y-3">
        {sortedLanguages.map(([lang, bytes]) => {
          const percentage = (bytes / totalBytes) * 100;
          return (
            <div key={lang} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getLanguageColor(lang) }}
                />
                <span className="text-sm font-medium">{lang}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  {t("Stats.languages.bytes", { value: formatBytes(bytes as any) })}
                </span>
                <span className="text-xs font-medium w-12 text-right">
                  {percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
